import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { listProducts, setProductActive, updateVariant, updateProduct, uploadProductImage, clearAdminPassword } from '../utils/api';

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await listProducts();
      setProducts(data.products);
    } catch (err) {
      if (err.status === 401) {
        clearAdminPassword();
        navigate('/');
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleActive(product) {
    try {
      await setProductActive(product.id, !product.active);
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, active: !product.active } : p)));
    } catch (err) {
      alert(`Failed to update: ${err.message}`);
    }
  }

  async function handleVariantSave(variantId, field, value, productId) {
    try {
      const result = await updateVariant(variantId, { [field]: value });
      setProducts((prev) =>
        prev.map((p) =>
          p.id !== productId
            ? p
            : {
                ...p,
                variants: p.variants.map((v) =>
                  v.id !== variantId ? v : { ...v, [field]: result.variant[field] }
                )
              }
        )
      );
      return true;
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
      return false;
    }
  }

  async function handleImageUpload(productId, file) {
    try {
      const result = await uploadProductImage(productId, file);
      setProducts(prev => prev.map(p => p.id !== productId ? p : { ...p, image_url: result.image_url }));
    } catch (err) {
      alert('Failed to upload image: ' + err.message);
    }
  }

  async function handleProductSave(productId, field, value) {
    try {
      const parsed = field === 'bulk_min_quantity' ? parseInt(value) : field === 'retail_price' || field === 'team_price' ? parseFloat(value) : value;
      if (parsed === null || parsed === undefined || (typeof parsed === 'number' && isNaN(parsed))) return false;
      const result = await updateProduct(productId, { [field]: parsed });
      setProducts((prev) => prev.map((p) => p.id !== productId ? p : { ...p, [field]: result.product[field] }));
      return true;
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
      return false;
    }
  }

  return (
    <Layout title="Products">
      <style>{css}</style>

      <div className="page-head">
        <h1 className="h1">Products</h1>
        <Link to="/products/import" className="btn-primary-sm">Import CSV</Link>
      </div>

      {loading && <div className="card"><p className="muted">Loading...</p></div>}
      {error && <div className="card" style={{ borderLeft: '4px solid #d70015' }}><p>{error}</p></div>}

      {!loading && !error && products.length === 0 && (
        <div className="card">
          <p className="muted">No products yet. <Link to="/products/import" className="inline-link">Import a CSV</Link> to get started.</p>
        </div>
      )}

      {!loading && products.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          onToggleActive={handleToggleActive}
          onVariantSave={handleVariantSave}
          onProductSave={handleProductSave}
          onImageUpload={handleImageUpload}
        />
      ))}
    </Layout>
  );
}

function ProductCard({ product, onToggleActive, onVariantSave, onProductSave, onImageUpload }) {
  return (
    <div className="card" style={{ opacity: product.active ? 1 : 0.55 }}>
      <div className="product-header">
        <div className="product-img-col">
          <label className="product-img-upload" title="Upload image">
            {product.image_url
              ? <img src={process.env.REACT_APP_API_URL + product.image_url} alt="" className="product-thumb" />
              : <span className="product-img-placeholder">+ Image</span>}
            <input type="file" accept="image/*" style={{display:'none'}} onChange={e => e.target.files[0] && onImageUpload(product.id, e.target.files[0])} />
          </label>
        </div>
        <div>
          <h2 className="product-name">{product.brand} {product.model}</h2>
          <p className="product-meta">
            {product.category} · {product.variants.length} variant{product.variants.length === 1 ? '' : 's'} · {product.total_stock} in stock
          </p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <button
            className={product.stick_type === 'goalie' ? 'pill pill-goalie' : 'pill pill-player'}
            onClick={() => onProductSave(product.id, 'stick_type', product.stick_type === 'goalie' ? 'player' : 'goalie')}
          >
            {product.stick_type === 'goalie' ? 'Goalie' : 'Player'}
          </button>
          <button
            onClick={() => onToggleActive(product)}
            className={product.active ? 'pill pill-active' : 'pill pill-inactive'}
          >
            {product.active ? 'Active' : 'Inactive'}
          </button>
        </div>
      </div>
      <div className="pricing-row">
        <div className="pricing-field">
          <span className="pricing-label">Retail Price</span>
          <EditableCell
            value={product.retail_price || ''}
            onSave={(val) => onProductSave(product.id, 'retail_price', parseFloat(val))}
            formatter={(val) => val ? `$${parseFloat(val).toFixed(2)}` : 'Set price'}
          />
        </div>
        <div className="pricing-field">
          <span className="pricing-label">Bulk Price</span>
          <EditableCell
            value={product.team_price || ''}
            onSave={(val) => onProductSave(product.id, 'team_price', parseFloat(val))}
            formatter={(val) => val ? `$${parseFloat(val).toFixed(2)}` : 'Set price'}
          />
        </div>
        <div className="pricing-field">
          <span className="pricing-label">Bulk Min Qty</span>
          <EditableCell
            value={product.bulk_min_quantity || ''}
            onSave={(val) => onProductSave(product.id, 'bulk_min_quantity', parseInt(val))}
            formatter={(val) => val ? `${val}+ units` : 'Set qty'}
          />
        </div>
      </div>

      <div className="table-wrap">
        <table className="variant-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Flex</th>
              <th>Hand</th>
              <th>Curve</th>
              <th>Stock</th>
            </tr>
          </thead>
          <tbody>
            {product.variants.map((v) => (
              <tr key={v.id}>
                <td>{v.sku}</td>
                <td>{v.flex}</td>
                <td>{v.hand}</td>
                <td>{v.blade_curve}</td>
                <td>
                  <EditableCell
                    value={v.stock_quantity}
                    onSave={(val) => onVariantSave(v.id, 'stock_quantity', val, product.id)}
                    formatter={(val) => String(val)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="variant-list-mobile">
        {product.variants.map((v) => (
          <div key={v.id} className="variant-mobile-card">
            <div className="variant-mobile-sku">{v.sku}</div>
            <div className="variant-mobile-row">
              <span className="variant-mobile-label">Flex</span><span>{v.flex}</span>
            </div>
            <div className="variant-mobile-row">
              <span className="variant-mobile-label">Hand</span><span>{v.hand}</span>
            </div>
            <div className="variant-mobile-row">
              <span className="variant-mobile-label">Curve</span><span>{v.blade_curve}</span>
            </div>
            <div className="variant-mobile-row">
              <span className="variant-mobile-label">Stock</span>
              <EditableCell
                value={v.stock_quantity}
                onSave={(val) => onVariantSave(v.id, 'stock_quantity', val, product.id)}
                formatter={(val) => String(val)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditableCell({ value, onSave, formatter }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));

  useEffect(() => { setDraft(String(value)); }, [value]);

  async function commit() {
    if (draft === String(value)) {
      setEditing(false);
      return;
    }
    const ok = await onSave(draft);
    if (ok) setEditing(false);
    else setDraft(String(value));
  }

  function handleKey(e) {
    if (e.key === 'Enter') commit();
    else if (e.key === 'Escape') {
      setDraft(String(value));
      setEditing(false);
    }
  }

  if (editing) {
    return (
      <input
        type="text"
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        className="cell-input"
      />
    );
  }

  return (
    <span className="editable-cell" onClick={() => setEditing(true)}>
      {formatter(value)}
    </span>
  );
}

const css = `
  .page-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    gap: 12px;
    flex-wrap: wrap;
  }
  .h1 { margin: 0; font-size: 28px; font-weight: 600; color: #1d1d1f; }
  .btn-primary-sm {
    background: #0071e3;
    color: #fff;
    padding: 8px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    border: none;
    cursor: pointer;
  }
  .card {
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 2px 20px rgba(0,0,0,0.06);
    padding: 24px;
    margin-bottom: 20px;
  }
  .muted { color: #86868b; font-size: 15px; margin: 0; }
  .inline-link { color: #0071e3; text-decoration: none; }
  .product-img-col { margin-right: 16px; flex-shrink: 0; }
  .product-img-upload { display: block; width: 72px; height: 72px; border-radius: 12px; border: 1.5px dashed #c0c0c8; cursor: pointer; overflow: hidden; display: flex; align-items: center; justify-content: center; background: #f5f5f7; transition: border-color 0.15s; }
  .product-img-upload:hover { border-color: #1a1a2e; }
  .product-thumb { width: 100%; height: 100%; object-fit: cover; }
  .product-img-placeholder { font-size: 12px; color: #aeaeb2; font-weight: 500; text-align: center; line-height: 1.3; }
  .pricing-row { display: flex; gap: 24px; padding: 12px 20px; background: #f9f9fb; border-bottom: 1px solid #e8e8ed; flex-wrap: wrap; }
  .pricing-field { display: flex; flex-direction: column; gap: 2px; }
  .pricing-label { font-size: 11px; font-weight: 600; letter-spacing: 0.07em; text-transform: uppercase; color: #aeaeb2; }
  .product-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
    gap: 12px;
    flex-wrap: wrap;
  }
  .product-name { margin: 0; font-size: 18px; font-weight: 600; color: #1d1d1f; }
  .product-meta { margin: 4px 0 0 0; color: #86868b; font-size: 13px; }
  .pill {
    padding: 6px 14px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
  }
  .pill-active { background: #e8f5e9; color: #1b5e20; border: 1px solid #c8e6c9; }
  .pill-player { padding: 5px 14px; border-radius: 100px; border: none; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; background: #e3f2fd; color: #1565c0; }
  .pill-goalie { padding: 5px 14px; border-radius: 100px; border: none; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; background: #f3e5f5; color: #6a1b9a; }
  .pill-inactive { background: #fef0f0; color: #86868b; border: 1px solid #f0d0d0; }
  .table-wrap { width: 100%; overflow-x: auto; }
  .variant-table { width: 100%; border-collapse: collapse; font-size: 14px; }
  .variant-table th {
    text-align: left; padding: 8px 12px; color: #86868b;
    font-weight: 500; font-size: 12px; text-transform: uppercase;
    border-bottom: 1px solid #f0f0f2;
  }
  .variant-table td { padding: 10px 12px; color: #1d1d1f; border-bottom: 1px solid #f5f5f7; }
  .editable-cell { cursor: pointer; padding: 4px 8px; border-radius: 6px; display: inline-block; }
  .cell-input {
    padding: 4px 8px; border: 1px solid #0071e3; border-radius: 6px;
    font-size: 14px; font-family: inherit; width: 80px; outline: none; box-sizing: border-box;
  }
  .variant-list-mobile { display: none; }
  .variant-mobile-card {
    border: 1px solid #f0f0f2; border-radius: 12px; padding: 12px 14px; margin-bottom: 10px;
  }
  .variant-mobile-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 4px 0; font-size: 14px; color: #1d1d1f;
  }
  .variant-mobile-sku {
    font-weight: 500; color: #1d1d1f; margin-bottom: 4px;
    border-bottom: 1px solid #f5f5f7; padding-bottom: 8px;
  }
  .variant-mobile-label { color: #86868b; font-size: 13px; }

  @media (max-width: 700px) {
    .h1 { font-size: 22px; }
    .card { padding: 16px; border-radius: 16px; }
    .table-wrap { display: none; }
    .variant-list-mobile { display: block; }
    .product-header { flex-direction: column; align-items: stretch; }
    .pill { align-self: flex-start; }
  }
`;
